import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Container, Spinner } from "reactstrap"
import SweetAlert from "react-bootstrap-sweetalert"
import { del, get, patch } from "helpers/api_helper"
import BandEditor from "./BandEditor"
import ContractsTable from "./ContractsTable"
import OffBandZone from "./OffBandZone"
import ExportBannerCard from "./ExportBannerCard"
import PartnerForm from "./PartnerForm"
import { buildPartnerPayload, partnerFormCountries, partnerWritePayload } from "./partnerPayload"
import {
  bandCapacity,
  bandTiers,
  movePartner,
  offBandPartners,
  orderForForm,
  restoredPartner,
  shiftPartner,
} from "./bandLayout"

// Parteneri — Sportya's own partners, the band above the footer of every page (ADR-0016). Event
// sponsors are the other model and are edited on the event, not here.
//
// The band at the top is the editor: it is a picture of the public surface, and arranging it is
// arranging the real thing. Everything the band cannot show sits under it — the contracts and
// markets it has no room for, and the partners it will not render at all.

// The write goes through the message broker, so the settings document is updated a moment after the
// request resolves. Re-read after a beat rather than claiming success on the response alone.
const REFETCH_DELAY_MS = 1500

const REARRANGED = "Band rearranged. Public pages pick it up within 15 minutes."

const Partners = () => {
  const mounted = useRef(true)
  const refetchTimer = useRef()
  const [partners, setPartners] = useState(null)
  // Frozen per load, so every "is this expired" question on one screenful answers consistently and
  // a page left open overnight re-reads the date when it re-reads the partners.
  const [now, setNow] = useState(() => new Date())
  const [loadError, setLoadError] = useState("")
  // null when closed; { partner, defaultTier } while adding or editing.
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  const load = useCallback(async () => {
    if (!mounted.current) return
    try {
      const response = await get("/admin/settings/partners")
      if (!mounted.current) return
      // '' for a category that has never been written.
      setPartners(Array.isArray(response?.data) ? response.data : [])
      setNow(new Date())
      setLoadError("")
    } catch (error) {
      if (!mounted.current) return
      setPartners([])
      setLoadError("Failed to load partners")
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    load()
    return () => {
      mounted.current = false
      clearTimeout(refetchTimer.current)
    }
  }, [load])

  const scheduleReload = () => {
    if (!mounted.current) return
    clearTimeout(refetchTimer.current)
    refetchTimer.current = setTimeout(load, REFETCH_DELAY_MS)
  }

  const rows = useMemo(() => partners || [], [partners])
  const tiers = useMemo(() => bandTiers(rows, now), [rows, now])
  const capacity = useMemo(() => bandCapacity(rows, now), [rows, now])
  const offBand = useMemo(() => offBandPartners(rows, now), [rows, now])
  // The band's own order, flattened: the table reads top to bottom exactly as the band reads left
  // to right, so an arrow in one and a drag in the other are visibly the same move.
  const live = useMemo(() => tiers.flatMap(({ rows: tier }) => tier), [tiers])

  /**
   * Writes partners back, one PATCH each — the endpoint takes a single item, so a drag that
   * renumbers a tier is several. They address different ids inside one document, so the order they
   * land in does not matter. The new list is applied locally first: an admin should see the plate
   * land where they dropped it, not a second and a half later.
   */
  const persist = async (next, writes, text) => {
    if (!writes.length) return
    setPartners(next)
    setMessage(null)
    try {
      await Promise.all(
        writes.map(partner => patch("/settings/partners", { data: partnerWritePayload(partner) }))
      )
      if (!mounted.current) return
      setMessage({ color: "success", text })
      scheduleReload()
    } catch (error) {
      if (!mounted.current) return
      setMessage({
        color: "danger",
        text: error?.response?.data?.message || "The change could not be saved — reloading.",
      })
      // The optimistic list is now a lie; the stored one is the truth.
      load()
    }
  }

  const move = ({ id, toTier, beforeId }) => {
    const { partners: next, writes } = movePartner(rows, { id, toTier, beforeId })
    persist(next, writes, REARRANGED)
  }

  const shift = (partner, direction) => {
    const { partners: next, writes } = shiftPartner(rows, partner.id, direction, now)
    persist(next, writes, REARRANGED)
  }

  const restore = partner => {
    const restored = restoredPartner(partner, now)
    persist(
      rows.map(row => (row.id === partner.id ? restored : row)),
      [restored],
      `${partner.name} is back on the band.`
    )
  }

  const savePartner = async form => {
    setSaving(true)
    setMessage(null)
    const payload = buildPartnerPayload({
      ...form,
      countries: partnerFormCountries(form),
      order: orderForForm(rows, form),
    })

    try {
      // No `id` is what makes the API mint one and append instead of replacing.
      await patch("/settings/partners", { data: payload })
      if (!mounted.current) return
      setEditing(null)
      setMessage({
        color: "success",
        text: `Saved — ${payload.name}. Public pages pick it up within 15 minutes.`,
      })
      scheduleReload()
    } catch (error) {
      if (!mounted.current) return
      setMessage({
        color: "danger",
        text: error?.response?.data?.message || "Failed to save the partner",
      })
    }
    if (mounted.current) setSaving(false)
  }

  const remove = async partner => {
    setRemoving(null)
    setMessage(null)
    try {
      await del(`/settings/partners/${partner.id}`)
      if (!mounted.current) return
      setPartners(rows.filter(row => row.id !== partner.id))
      setMessage({ color: "success", text: `${partner.name} was removed.` })
      scheduleReload()
    } catch (error) {
      if (!mounted.current) return
      setMessage({
        color: "danger",
        text: error?.response?.data?.message || "Failed to remove the partner",
      })
      load()
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <h4>Parteneri</h4>
        <p className="text-muted">
          Sportya&apos;s own partners — the band above the footer of every page. Not event sponsors:
          those are edited on the event itself.
        </p>

        {loadError && <Alert color="danger">{loadError}</Alert>}
        {message && <Alert color={message.color}>{message.text}</Alert>}

        {partners === null ? (
          <Spinner size="sm" animation="grow" variant="dark" />
        ) : (
          <>
            <BandEditor
              tiers={tiers}
              capacity={capacity}
              onMove={move}
              onEdit={partner => setEditing({ partner })}
              onAdd={tier => setEditing({ partner: null, defaultTier: tier })}
            />

            <ContractsTable
              partners={live}
              now={now}
              onEdit={partner => setEditing({ partner })}
              onRetier={(partner, tier) => move({ id: partner.id, toTier: tier, beforeId: null })}
              onShift={shift}
            />

            <OffBandZone
              partners={offBand}
              now={now}
              onRestore={restore}
              onRemove={setRemoving}
            />
          </>
        )}

        {/* Not part of the band: the export carries its own image, and the card says so. It loads
            on its own, so a slow partners read does not hold it up (or the other way round). */}
        <ExportBannerCard />

        {/* Both overlays live at the end, out of the page's flow — they cover the band rather than
            pushing it about. */}
        {editing && (
          <PartnerForm
            // Remounts on a different partner: the form owns its fields, and FileUpload owns its
            // preview with no way to reset it from outside.
            key={editing.partner?.id || `new-${editing.defaultTier}`}
            partner={editing.partner}
            defaultTier={editing.defaultTier}
            saving={saving}
            onSave={savePartner}
            onCancel={() => setEditing(null)}
          />
        )}

        {removing && (
          <SweetAlert
            warning
            title="Remove partner"
            showCancel
            confirmBtnText="Remove"
            confirmBtnBsStyle="danger"
            onConfirm={() => remove(removing)}
            onCancel={() => setRemoving(null)}
          >
            {removing.name} will be deleted for good. To keep the record and only take it off the
            band, restore it and untick &quot;Show on the band&quot; instead.
          </SweetAlert>
        )}
      </Container>
    </div>
  )
}

export default Partners
